import { ComponentProps, FC, ReactNode, useEffect, useState } from "react";
import { Layout, Grid } from "antd";
import * as _ from "lodash";

import { classname } from "@ctra/utils";

import { CtraLogo } from "../../../elements";
import styles from "./ApplicationWrapper.module.less";

const { Content, Sider, Header, Footer } = Layout;

export interface ApplicationWrapperProps extends ComponentProps<typeof Layout> {
  /**
   * sandbox mode
   */
  sandboxMode?: boolean;
  /**
   * dialog mode
   */
  dialogMode?: boolean;
  /**
   * components which will be rendered on top of the application (dialogs, modals, non-visuals, etc.)
   */
  floaters?: ReactNode;
  /**
   * components which will be rendered inside the sider
   */
  sider?:
    | ReactNode
    | (({
        isCollapsed,
        toggle
      }: {
        isCollapsed: boolean;
        toggle: (collapsed: boolean) => void;
      }) => ReactNode);
  /**
   * handler for sider collapse event
   * @param {boolean} collapsed
   */
  onSiderCollapse?: (collapsedBefore: boolean, collapsedAfter: boolean) => void;
  /**
   * components which will be rendered inside the header
   */
  header?: ReactNode;
  /**
   * components which will be rendered inside the footer
   */
  footer?: ReactNode;
}

/**
 * Application wrapper component with sidebar, header and footer
 * @param {string | undefined} className
 * @param {boolean | undefined} sandboxMode
 * @param {React.ReactElement<any, string | React.JSXElementConstructor<any>> | string | number | {} | Iterable<React.ReactNode> | React.ReactPortal | boolean | null | undefined} floaters
 * @param {React.ReactElement<any, string | React.JSXElementConstructor<any>> | string | number | {} | Iterable<React.ReactNode> | React.ReactPortal | boolean | null | undefined | (({isCollapsed, toggle}: {isCollapsed: boolean, toggle: (collapsed: boolean) => void}) => React.ReactNode)} sider
 * @param {((collapsedBefore: boolean, collapsedAfter: boolean) => void) | undefined} onSiderCollapse
 * @param {React.ReactElement<any, string | React.JSXElementConstructor<any>> | string | number | {} | Iterable<React.ReactNode> | React.ReactPortal | boolean | null | undefined} header
 * @param {React.ReactElement<any, string | React.JSXElementConstructor<any>> | string | number | {} | Iterable<React.ReactNode> | React.ReactPortal | boolean | null | undefined} footer
 * @param {React.ReactElement<any, string | React.JSXElementConstructor<any>> | string | number | {} | Iterable<React.ReactNode> | React.ReactPortal | boolean | null | undefined} children
 * @param {Pick<ApplicationWrapperProps & {children?: React.ReactNode | undefined}, "prefixCls" | "hasSider" | "defaultChecked" | "defaultValue" | "suppressContentEditableWarning" | "suppressHydrationWarning" | "accessKey" | "contentEditable" | "contextMenu" | "dir" | "draggable" | "hidden" | "id" | "lang" | "placeholder" | "slot" | "spellCheck" | "style" | "tabIndex" | "title" | "translate" | "radioGroup" | "role" | "about" | "datatype" | "inlist" | "prefix" | "property" | "resource" | "typeof" | "vocab" | "autoCapitalize" | "autoCorrect" | "autoSave" | "color" | "itemProp" | "itemScope" | "itemType" | "itemID" | "itemRef" | "results" | "security" | "unselectable" | "inputMode" | "is" | "aria-activedescendant" | "aria-atomic" | "aria-autocomplete" | "aria-busy" | "aria-checked" | "aria-colcount" | "aria-colindex" | "aria-colspan" | "aria-controls" | "aria-current" | "aria-describedby" | "aria-details" | "aria-disabled" | "aria-dropeffect" | "aria-errormessage" | "aria-expanded" | "aria-flowto" | "aria-grabbed" | "aria-haspopup" | "aria-hidden" | "aria-invalid" | "aria-keyshortcuts" | "aria-label" | "aria-labelledby" | "aria-level" | "aria-live" | "aria-modal" | "aria-multiline" | "aria-multiselectable" | "aria-orientation" | "aria-owns" | "aria-placeholder" | "aria-posinset" | "aria-pressed" | "aria-readonly" | "aria-relevant" | "aria-required" | "aria-roledescription" | "aria-rowcount" | "aria-rowindex" | "aria-rowspan" | "aria-selected" | "aria-setsize" | "aria-sort" | "aria-valuemax" | "aria-valuemin" | "aria-valuenow" | "aria-valuetext" | "dangerouslySetInnerHTML" | "onCopy" | "onCopyCapture" | "onCut" | "onCutCapture" | "onPaste" | "onPasteCapture" | "onCompositionEnd" | "onCompositionEndCapture" | "onCompositionStart" | "onCompositionStartCapture" | "onCompositionUpdate" | "onCompositionUpdateCapture" | "onFocus" | "onFocusCapture" | "onBlur" | "onBlurCapture" | "onChange" | "onChangeCapture" | "onBeforeInput" | "onBeforeInputCapture" | "onInput" | "onInputCapture" | "onReset" | "onResetCapture" | "onSubmit" | "onSubmitCapture" | "onInvalid" | "onInvalidCapture" | "onLoad" | "onLoadCapture" | "onError" | "onErrorCapture" | "onKeyDown" | "onKeyDownCapture" | "onKeyPress" | "onKeyPressCapture" | "onKeyUp" | "onKeyUpCapture" | "onAbort" | "onAbortCapture" | "onCanPlay" | "onCanPlayCapture" | "onCanPlayThrough" | "onCanPlayThroughCapture" | "onDurationChange" | "onDurationChangeCapture" | "onEmptied" | "onEmptiedCapture" | "onEncrypted" | "onEncryptedCapture" | "onEnded" | "onEndedCapture" | "onLoadedData" | "onLoadedDataCapture" | "onLoadedMetadata" | "onLoadedMetadataCapture" | "onLoadStart" | "onLoadStartCapture" | "onPause" | "onPauseCapture" | "onPlay" | "onPlayCapture" | "onPlaying" | "onPlayingCapture" | "onProgress" | "onProgressCapture" | "onRateChange" | "onRateChangeCapture" | "onSeeked" | "onSeekedCapture" | "onSeeking" | "onSeekingCapture" | "onStalled" | "onStalledCapture" | "onSuspend" | "onSuspendCapture" | "onTimeUpdate" | "onTimeUpdateCapture" | "onVolumeChange" | "onVolumeChangeCapture" | "onWaiting" | "onWaitingCapture" | "onAuxClick" | "onAuxClickCapture" | "onClick" | "onClickCapture" | "onContextMenu" | "onContextMenuCapture" | "onDoubleClick" | "onDoubleClickCapture" | "onDrag" | "onDragCapture" | "onDragEnd" | "onDragEndCapture" | "onDragEnter" | "onDragEnterCapture" | "onDragExit" | "onDragExitCapture" | "onDragLeave" | "onDragLeaveCapture" | "onDragOver" | "onDragOverCapture" | "onDragStart" | "onDragStartCapture" | "onDrop" | "onDropCapture" | "onMouseDown" | "onMouseDownCapture" | "onMouseEnter" | "onMouseLeave" | "onMouseMove" | "onMouseMoveCapture" | "onMouseOut" | "onMouseOutCapture" | "onMouseOver" | "onMouseOverCapture" | "onMouseUp" | "onMouseUpCapture" | "onSelect" | "onSelectCapture" | "onTouchCancel" | "onTouchCancelCapture" | "onTouchEnd" | "onTouchEndCapture" | "onTouchMove" | "onTouchMoveCapture" | "onTouchStart" | "onTouchStartCapture" | "onPointerDown" | "onPointerDownCapture" | "onPointerMove" | "onPointerMoveCapture" | "onPointerUp" | "onPointerUpCapture" | "onPointerCancel" | "onPointerCancelCapture" | "onPointerEnter" | "onPointerEnterCapture" | "onPointerLeave" | "onPointerLeaveCapture" | "onPointerOver" | "onPointerOverCapture" | "onPointerOut" | "onPointerOutCapture" | "onGotPointerCapture" | "onGotPointerCaptureCapture" | "onLostPointerCapture" | "onLostPointerCaptureCapture" | "onScroll" | "onScrollCapture" | "onWheel" | "onWheelCapture" | "onAnimationStart" | "onAnimationStartCapture" | "onAnimationEnd" | "onAnimationEndCapture" | "onAnimationIteration" | "onAnimationIterationCapture" | "onTransitionEnd" | "onTransitionEndCapture" | "ref" | "key">} rest
 * @return {JSX.Element}
 * @constructor
 */
const ApplicationWrapper: FC<ApplicationWrapperProps> = ({
  className,
  sandboxMode,
  dialogMode,
  floaters,
  sider,
  onSiderCollapse,
  header,
  footer,
  children,
  ...rest
}) => {
  const { md, lg } = Grid.useBreakpoint();
  const [collapsedSider, setCollapsedSider] = useState<boolean>(!_.isUndefined(lg) && !lg);

  /**
   * auto collapse sidebar on mobile
   */
  useEffect(() => {
    setCollapsedSider(!lg);
  }, [lg]);

  return (
    <Layout
      className={classname("ctra-generic-applicationWrapper", styles.ApplicationWrapper, className)}
      {...rest}
    >
      {/* floater components */}
      {_.defaultTo(floaters, null)}

      {/* sidebar */}
      {sider && md ? (
        <Sider
          width={220}
          collapsedWidth={64}
          collapsible
          collapsed={_.defaultTo(collapsedSider, void 0)}
          className={styles.SiderWrapper}
          onCollapse={(collapsed, type) => {
            if (type === "clickTrigger") {
              if (_.isFunction(onSiderCollapse)) {
                onSiderCollapse(collapsedSider, collapsed); // trackEvent(collapsedSider ? GAActions.collapseSidebar : GAActions.expandSidebar);
              }

              setCollapsedSider(collapsed);
            }
          }}
        >
          <Layout className={styles.SiderContent}>
            <Header className={styles.Logo}>
              <CtraLogo
                variant={collapsedSider ? "noText" : "withText"}
                className={collapsedSider ? "no-text" : "with-text"}
              />
            </Header>
            <Content>
              {_.isFunction(sider)
                ? sider({ isCollapsed: collapsedSider, toggle: (collapsed) => setCollapsedSider(collapsed) })
                : sider}
            </Content>
          </Layout>
        </Sider>
      ) : null}

      {/* main content */}
      <Content>
        <Layout style={{ minHeight: "100vh" }}>
          {header ? (
            <Header
              className={classname(
                styles.HeaderWrapper,
                sandboxMode ? styles.Sandbox : dialogMode ? styles.Dialog : null
              )}
            >
              {header}
            </Header>
          ) : null}
          <Content className={classname(styles.PageContent)}>
            <Layout>
              <Content>{children}</Content>
            </Layout>
          </Content>
          {!md && footer ? <Footer className={styles.FooterWrapper}>{footer}</Footer> : null}
        </Layout>
      </Content>
    </Layout>
  );
};

export default ApplicationWrapper;
